<template>
    <div v-if="open" class="cv3-modal-overlay" @click.self="close">
        <div class="cv3-modal">
            <header class="cv3-modal-head">
                <div class="cv3-modal-title">
                    <span class="cv3-modal-icon"><CreditCardIcon :size="18" /></span>
                    <h2>{{ $t('checkout.v3.payment_title') }}</h2>
                </div>
                <button type="button" class="cv3-modal-close" @click="close"><XIcon :size="20" /></button>
            </header>

            <div class="cv3-modal-body">
                <!-- LEFT : recap -->
                <div class="cv3-recap">
                    <div class="cv3-recap-head">
                        <span class="cv3-recap-label">{{ $t('checkout.v3.recap_title') }}</span>
                        <span class="cv3-recap-client">{{ clientName }}</span>
                    </div>
                    <div class="cv3-recap-lines">
                        <div v-for="l in store.lines" :key="l.rowId" class="cv3-recap-line">
                            <div class="cv3-recap-line-main">
                                <span class="cv3-recap-qty">{{ l.qty }}×</span>
                                <span class="cv3-recap-name">{{ l.name }}</span>
                                <!-- Remise persistée sur la ligne : on montre le prix d'origine barré
                                     et le prix remisé, au total comme à l'unité. -->
                                <span
                                    v-if="l.hasDiscount && store.promotion"
                                    class="cv3-recap-promo"
                                    :title="store.promotion.title"
                                >
                                    <TagIcon :size="11" /> {{ promoDisplay(store.promotion) }}
                                </span>
                            </div>
                            <span class="cv3-recap-total">
                                <s v-if="l.hasDiscount" class="cv3-recap-old">{{ formatPrice(l.qty * l.price) }} €</s>
                                <span>{{ formatPrice(l.hasDiscount ? l.lineNet : l.qty * l.price) }} €</span>
                            </span>
                            <span class="cv3-recap-unit">
                                <s v-if="l.hasDiscount" class="cv3-recap-old">{{ formatPrice(l.price) }} €</s>
                                {{ formatPrice(l.hasDiscount ? l.unitNet : l.price) }} € / u
                            </span>
                        </div>
                    </div>
                    <!-- Déjà payé (acomptes) + remboursement total/partiel -->
                    <div v-if="store.invoices.length" class="cv3-deja">
                        <p class="cv3-deja-label">{{ $t('checkout.v3.already_paid_label') }}</p>
                        <div v-for="inv in store.invoices" :key="inv.id" class="cv3-deja-row">
                            <span class="cv3-deja-info"
                                >{{ inv.method }}<small> · {{ inv.date }}</small></span
                            >
                            <span class="cv3-deja-amount">{{ formatPrice(inv.amount) }} €</span>
                            <!-- Vente clôturée : lecture seule totale — aucun remboursement depuis la
                                 caisse. Les avoirs se font depuis la page factures. -->
                            <span v-if="!locked" class="cv3-refund" @click.stop>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    :max="inv.amount"
                                    v-model.number="refundAmounts[inv.id]"
                                />
                                <button type="button" class="cv3-refund-btn" :disabled="store.busy" @click="refund(inv)">
                                    {{ $t('checkout.v3.refund_btn') }}
                                </button>
                            </span>
                        </div>
                    </div>

                    <div v-if="appliedPromo" class="cv3-recap-remise">
                        <span>{{ $t('checkout.v3.discount_label') }} ({{ promoDisplay(appliedPromo) }})</span>
                        <span>−{{ formatPrice(promoDiscount) }} €</span>
                    </div>
                    <div v-if="store.currentPrice > 0" class="cv3-recap-remise" style="color: var(--cv3-text-2)">
                        <span>{{ $t('checkout.v3.already_collected') }}</span>
                        <span>−{{ formatPrice(store.currentPrice) }} €</span>
                    </div>
                    <div class="cv3-recap-foot">
                        <span>{{
                            store.currentPrice > 0
                                ? $t('checkout.v3.reste_label')
                                : appliedPromo
                                  ? $t('checkout.v3.to_pay_label')
                                  : $t('checkout.v3.total_ttc')
                        }}</span>
                        <span class="cv3-recap-grand">{{ formatPrice(Math.max(0, effectiveTotal - store.currentPrice)) }} €</span>
                    </div>
                </div>

                <!-- RIGHT : payment methods -->
                <div class="cv3-pay">
                    <!-- Vente clôturée : lecture seule totale (NF525) — ni encaissement ni remboursement
                         depuis la caisse. Les avoirs se font depuis la page factures. -->
                    <div v-if="locked" class="cv3-locked-banner">
                        <LockIcon :size="15" />
                        <span>{{ $t('checkout.v3.closed_readonly_payment') }}</span>
                    </div>

                    <!-- Trop-perçu : le déjà-payé (acompte) dépasse le net dû — typiquement une promo qui
                         ramène le total sous l'acompte encaissé. On avertit et on bloque la clôture tant
                         que l'excédent n'est pas remboursé (le remboursement est dans « Déjà payé »). -->
                    <div v-if="!locked && overpaid > 0.009" class="cv3-overpaid-banner">
                        <AlertTriangleIcon :size="16" />
                        <span>{{ $t('checkout.v3.overpaid_warning', { amount: formatPrice(overpaid) }) }}</span>
                    </div>

                    <div class="cv3-status" :class="solde ? 'solde' : 'reste'">
                        <span class="cv3-status-label">{{
                            solde ? $t('checkout.v3.paid_status') : $t('checkout.v3.remaining_status')
                        }}</span>
                        <span class="cv3-status-val">
                            <CheckIcon v-if="solde" :size="26" />
                            <template v-else>{{ formatPrice(remaining) }} €</template>
                        </span>
                    </div>

                    <div :class="{ 'cv3-pay-locked': locked }">
                        <!-- Promo / réduction -->
                        <div class="cv3-promo">
                            <div v-if="!appliedPromo" class="cv3-promo-pick">
                                <select v-model="selectedPromoCode" class="cv3-stripe-card">
                                    <option value="">{{ $t('checkout.v3.promo_placeholder') }}</option>
                                    <optgroup v-if="reductionPromos.length" :label="$t('checkout.v3.reductions_group')">
                                        <option v-for="p in reductionPromos" :key="'r' + p.id" :value="p.code">
                                            {{ promoLabel(p) }}
                                        </option>
                                    </optgroup>
                                    <optgroup v-if="otherPromos.length" :label="$t('checkout.v3.promos_group')">
                                        <option v-for="p in otherPromos" :key="'o' + p.id" :value="p.code">
                                            {{ promoLabel(p) }}
                                        </option>
                                    </optgroup>
                                </select>
                                <button
                                    type="button"
                                    class="cv3-mini-btn"
                                    :disabled="!selectedPromoCode || promoBusy"
                                    @click="applyPromo"
                                >
                                    {{ $t('common.apply') }}
                                </button>
                            </div>
                            <!-- Remise instantanée : la caisse saisit un montant (€) ou un pourcentage (%),
                                 le serveur crée un code promo caché à usage unique et l'applique. Jamais
                                 listé, jamais réutilisable. -->
                            <div v-if="!appliedPromo" class="cv3-promo-custom">
                                <select v-model="customDiscountType" class="cv3-custom-type">
                                    <option value="fixed">€</option>
                                    <option value="percentage">%</option>
                                </select>
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    v-model.number="customDiscountAmount"
                                    :placeholder="$t('checkout.v3.custom_discount_placeholder')"
                                    @keyup.enter="applyCustomDiscount"
                                />
                                <button
                                    type="button"
                                    class="cv3-mini-btn"
                                    :disabled="!validCustomDiscount || promoBusy"
                                    @click="applyCustomDiscount"
                                >
                                    {{ $t('common.apply') }}
                                </button>
                            </div>
                            <div v-else class="cv3-promo-applied">
                                <span class="cv3-promo-tag" :title="appliedPromo.title"
                                    ><TagIcon :size="14" /> {{ promoDisplay(appliedPromo) }} · −{{
                                        formatPrice(promoDiscount)
                                    }}
                                    €</span
                                >
                                <button type="button" class="cv3-clear" :disabled="promoBusy" @click="removePromo">
                                    <XIcon :size="14" />
                                </button>
                            </div>
                        </div>

                        <p class="cv3-pay-label">{{ $t('checkout.v3.payment_methods_label') }}</p>

                        <p v-if="!methods.length" class="cv3-empty">{{ $t('checkout.v3.no_payment_methods') }}</p>

                        <button
                            v-for="m in recordableMethods"
                            :key="m.name"
                            type="button"
                            class="cv3-pay-row primary"
                            :class="{ filled: amounts[m.name] > 0 }"
                            @click="fill(m.name)"
                        >
                            <span class="cv3-pay-name"><component :is="m.icon" :size="18" /> {{ m.label }}</span>
                            <span class="cv3-pay-amount" @click.stop>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    v-model.number="amounts[m.name]"
                                    @focus="$event.target.select()"
                                />
                                <span class="cv3-cur">€</span>
                                <button v-if="amounts[m.name] > 0" type="button" class="cv3-clear" @click.stop="clear(m.name)">
                                    <XIcon :size="14" />
                                </button>
                            </span>
                        </button>

                        <template v-for="m in specialMethods">
                            <!-- Bon cadeau : redemption by code -->
                            <div v-if="m.name === 'Bon cadeau'" :key="'meth-voucher-' + m.name" class="cv3-special-block">
                                <div class="cv3-special-head"><GiftIcon :size="16" /> {{ m.label }}</div>
                                <div class="cv3-voucher-entry">
                                    <input
                                        v-model="voucherCode"
                                        type="text"
                                        :placeholder="$t('checkout.v3.voucher_code_placeholder')"
                                        @keyup.enter="verifyVoucher"
                                    />
                                    <button type="button" :disabled="voucherBusy || !voucherCode" @click="verifyVoucher">
                                        {{ $t('checkout.v3.verify_btn') }}
                                    </button>
                                </div>
                                <div v-for="(v, i) in vouchers" :key="v.id" class="cv3-voucher-line">
                                    <span class="cv3-voucher-code"
                                        >{{ v.code
                                        }}<small>
                                            ·
                                            {{
                                                $t('checkout.v3.wallet_balance', { balance: formatPrice(v.available) + ' €' })
                                            }}</small
                                        ></span
                                    >
                                    <span class="cv3-pay-amount">
                                        <input type="number" step="0.01" min="0" :max="v.available" v-model.number="v.amount" />
                                        <span class="cv3-cur">€</span>
                                        <button type="button" class="cv3-clear" @click="removeVoucher(i)">
                                            <XIcon :size="14" />
                                        </button>
                                    </span>
                                </div>
                            </div>

                            <!-- Stripe : saved-card charge -->
                            <div v-else-if="m.name === 'Stripe'" :key="'meth-stripe-' + m.name" class="cv3-special-block">
                                <div class="cv3-special-head"><ZapIcon :size="16" /> {{ m.label }}</div>
                                <button
                                    v-if="!cardsLoaded"
                                    type="button"
                                    class="cv3-mini-btn"
                                    :disabled="cardsLoading"
                                    @click="loadCards"
                                >
                                    {{ cardsLoading ? $t('common.loading') : $t('checkout.v3.saved_cards_btn') }}
                                </button>
                                <template v-else>
                                    <p v-if="!cards.length" class="cv3-soon-text">{{ $t('checkout.v3.no_saved_cards') }}</p>
                                    <div v-else class="cv3-stripe">
                                        <select v-model="selectedCard" class="cv3-stripe-card">
                                            <option :value="null" disabled>
                                                {{ $t('checkout.v3.choose_card_placeholder') }}
                                            </option>
                                            <option v-for="c in cards" :key="c.id" :value="c.id">
                                                •••• {{ c.last4 }} ({{ c.brand }})
                                            </option>
                                        </select>
                                        <span class="cv3-pay-amount">
                                            <input type="number" step="0.01" min="0" v-model.number="stripeAmount" />
                                            <span class="cv3-cur">€</span>
                                        </span>
                                    </div>
                                </template>
                            </div>

                            <!-- Wallet : pay from the customer's cagnotte (capped to balance) -->
                            <div v-else-if="m.name === 'Wallet'" :key="'meth-wallet-' + m.name" class="cv3-special-block">
                                <div class="cv3-special-head">
                                    <WalletIcon :size="16" /> Wallet
                                    <small class="cv3-bal">{{
                                        $t('checkout.v3.wallet_balance', { balance: formatPrice(store.walletBalance) + ' €' })
                                    }}</small>
                                </div>
                                <div v-section-row class="cv3-special-row">
                                    <span class="cv3-pay-amount">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            v-model.number="walletAmount"
                                            @input="capWallet"
                                            @focus="$event.target.select()"
                                        />
                                        <span class="cv3-cur">€</span>
                                    </span>
                                    <button
                                        type="button"
                                        class="cv3-mini-btn"
                                        :disabled="store.walletBalance <= 0"
                                        @click="fillWallet"
                                    >
                                        {{ $t('checkout.v3.use_balance_btn') }}
                                    </button>
                                </div>
                            </div>

                            <!-- other special tenders : not yet wired -->
                            <div
                                v-else
                                :key="'meth-other-' + m.name"
                                class="cv3-pay-row grid disabled"
                                :title="$t('checkout.v3.dedicated_flow_soon')"
                            >
                                <span class="cv3-pay-name"><component :is="m.icon" :size="16" /> {{ m.label }}</span>
                                <span class="cv3-pay-amount"
                                    ><span class="cv3-soon">{{ $t('common.soon') }}</span></span
                                >
                            </div>
                        </template>

                        <!-- Fidélité : spend loyalty points. Shown whenever the loyalty program is enabled
                         (is_loyalty_program_enabled) — NOT tied to the establishment's payment-method list, per legacy. -->
                        <div v-if="store.loyaltyEnabled" class="cv3-special-block">
                            <div class="cv3-special-head">
                                <StarIcon :size="16" /> {{ $t('checkout.v3.fidelity_label') }}
                                <small class="cv3-bal">{{
                                    $t('checkout.v3.fidelity_balance', {
                                        points: store.loyaltyPoints,
                                        amount: formatPrice(fidelityMaxEuro) + ' €',
                                    })
                                }}</small>
                            </div>
                            <p v-if="store.loyaltyPoints <= 0" class="cv3-soon-text">{{ $t('checkout.v3.no_points') }}</p>
                            <div v-else class="cv3-special-row">
                                <span class="cv3-pay-amount">
                                    <input
                                        type="number"
                                        min="0"
                                        :max="store.loyaltyPoints"
                                        v-model.number="fidelityPoints"
                                        @input="capFidelity"
                                        @focus="$event.target.select()"
                                    />
                                    <span class="cv3-cur">pts</span>
                                </span>
                                <span class="cv3-eur-eq">≈ {{ formatPrice(fidelityEuro) }} €</span>
                            </div>
                        </div>

                        <!-- Prepaid series / cures the customer already owns (decrements sessions, no cash) -->
                        <PrepaidPanel :store="store" :open="open" @change="onPrepaidChange" />

                        <!-- Clôture : facture société + mail de remerciement (legacy CloseSaleModal) -->
                        <div class="cv3-special-block cv3-invoice">
                            <div class="cv3-check ppv2-flex ppv2-items-center ppv2-gap-2">
                                <BaseCheckbox v-model="invoice.send_invoice" has-border-checked />
                                <FileTextIcon :size="16" />
                                <span>{{ $t('checkout.v3.invoice_company_label') }}</span>
                            </div>
                            <template v-if="invoice.send_invoice">
                                <input
                                    v-model="invoice.company_name"
                                    type="text"
                                    class="cv3-inv-input"
                                    :placeholder="$t('common.steps.parameters.company_name')"
                                />
                                <input
                                    v-model="invoice.company_address"
                                    type="text"
                                    class="cv3-inv-input"
                                    :placeholder="$t('common.address')"
                                />
                                <div class="cv3-inv-row">
                                    <input
                                        v-model="invoice.company_cp"
                                        type="text"
                                        class="cv3-inv-input"
                                        :placeholder="$t('common.postal_code')"
                                    />
                                    <input
                                        v-model="invoice.company_country"
                                        type="text"
                                        class="cv3-inv-input"
                                        :placeholder="$t('common.country')"
                                    />
                                </div>
                            </template>
                            <div class="cv3-check ppv2-flex ppv2-items-center ppv2-gap-2">
                                <BaseCheckbox v-model="sendMail" has-border-checked />
                                <MailIcon :size="16" />
                                <span>{{ $t('checkout.v3.send_thank_you_email') }}</span>
                            </div>
                            <div v-if="sendMail" class="cv3-check sub ppv2-flex ppv2-items-center ppv2-gap-2">
                                <BaseCheckbox v-model="notice" has-border-checked />
                                <span>{{ $t('checkout.v3.include_review_links') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Une vente clôturée ne peut plus être ni encaissée ni re-clôturée : on retire les deux
                 actions. Le remboursement reste accessible depuis « Déjà payé » (colonne de gauche). -->
            <footer v-if="!locked" class="cv3-modal-foot">
                <button type="button" class="cv3-acompte-btn" :disabled="!canEncaisser" @click="validate(false)">
                    {{ solde ? $t('checkout.v3.cash_in_btn') : $t('checkout.v3.cash_in_acompte') }}
                </button>
                <button type="button" class="cv3-validate-pay" :disabled="!canCloturer" @click="requestClose">
                    <CheckIcon :size="18" /> {{ $t('checkout.v3.close_sale_btn') }}
                </button>
            </footer>
            <footer v-else class="cv3-modal-foot">
                <button type="button" class="cv3-validate-pay" @click="close">
                    {{ $t('common.close') }}
                </button>
            </footer>

            <!-- Confirmation de clôture demandée uniquement en cas de trop-perçu (le client a payé plus
                 que le net dû). Popup in-app (jamais un dialog navigateur). Confirmer → clôture ;
                 Annuler → retour à la modale pour rembourser l'excédent d'abord. -->
            <div v-if="confirmCloseOpen" class="cv3-confirm-overlay" @click.self="confirmCloseOpen = false">
                <div class="cv3-confirm">
                    <div class="cv3-confirm-head">
                        <AlertTriangleIcon :size="20" />
                        <h3>{{ $t('checkout.v3.confirm_close_title') }}</h3>
                    </div>
                    <p class="cv3-confirm-msg">
                        {{ $t('checkout.v3.confirm_close_overpaid', { amount: formatPrice(overpaid) }) }}
                    </p>
                    <div class="cv3-confirm-actions">
                        <button type="button" class="cv3-confirm-cancel" @click="confirmCloseOpen = false">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="button" class="cv3-confirm-ok" :disabled="store.busy" @click="confirmClose">
                            {{ $t('checkout.v3.confirm_close_anyway') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    CreditCard as CreditCardIcon,
    X as XIcon,
    Check as CheckIcon,
    Banknote as BanknoteIcon,
    Zap as ZapIcon,
    FileText as FileTextIcon,
    Building as BuildingIcon,
    Gift as GiftIcon,
    Wallet as WalletIcon,
    Ticket as TicketIcon,
    ShieldCheck as ShieldCheckIcon,
    Star as StarIcon,
    Utensils as UtensilsIcon,
    Percent as PercentIcon,
    Tag as TagIcon,
    Lock as LockIcon,
    AlertTriangle as AlertTriangleIcon,
    Mail as MailIcon,
} from 'lucide-vue'
import PrepaidPanel from './PrepaidPanel.vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'

const SPECIAL = ['Bon cadeau', 'Stripe', 'Wallet', 'Points de fidélité']
// Money Banking : tender proposé sur le POS uniquement.
const HIDDEN = ['cure', 'serie', 'Abonnement', 'Billet non daté', 'Points de fidélité', 'Money Banking']

const ICONS = {
    Espèces: BanknoteIcon,
    CB: CreditCardIcon,
    Stripe: ZapIcon,
    Chèque: FileTextIcon,
    Virement: BuildingIcon,
    'Bon cadeau': GiftIcon,
    Wallet: WalletIcon,
    ANCV: TicketIcon,
    'Pass culture': ShieldCheckIcon,
    'Points de fidélité': StarIcon,
    'Tickets restaurant': UtensilsIcon,
    'Remise personnalisée': PercentIcon,
}
const LABELS = {
    CB: 'Carte bancaire',
    'Pass culture': 'Pass Culture',
    'Points de fidélité': 'Fidélité',
}

export default {
    name: 'PaymentDialog',
    components: {
        CreditCardIcon,
        XIcon,
        LockIcon,
        AlertTriangleIcon,
        CheckIcon,
        GiftIcon,
        ZapIcon,
        WalletIcon,
        StarIcon,
        TagIcon,
        FileTextIcon,
        MailIcon,
        PrepaidPanel,
        BaseCheckbox,
    },
    props: {
        store: { type: Object, required: true },
        open: { type: Boolean, default: false },
    },
    data() {
        return {
            amounts: {}, // keyed by PaymentMethod.name (recordable methods only)
            vouchers: [], // [{ id, code, available, amount }]
            voucherCode: '',
            voucherBusy: false,
            cards: [],
            cardsLoaded: false,
            cardsLoading: false,
            selectedCard: null,
            stripeAmount: 0,
            walletAmount: 0,
            fidelityPoints: 0,
            selectedPromoCode: '',
            // Remise instantanée : type (fixed € / percentage %) + montant saisis par la caisse.
            customDiscountType: 'fixed',
            customDiscountAmount: null,
            appliedPromo: null,
            // true when appliedPromo came from the order's persisted pivot rows rather than from a
            // code the cashier just picked here. Drives which discount number we trust (see promoDiscount).
            promoFromServer: false,
            promoBusy: false,
            confirmCloseOpen: false, // popup de confirmation de clôture (trop-perçu)
            refundAmounts: {},
            prepaid: { prepaidSerie: [], prepaidTreatment: [], coverage: 0 },
            // Clôture : facture société + mail de remerciement (legacy CloseSaleModal, rebooking abandonné).
            invoice: { send_invoice: false, company_name: '', company_address: '', company_cp: '', company_country: '' },
            sendMail: false,
            notice: false,
        }
    },
    computed: {
        methods() {
            return (this.store.paymentMethods || [])
                .filter((m) => !HIDDEN.includes(m.name))
                .map((m) => ({
                    name: m.name,
                    label: LABELS[m.name] || m.name,
                    icon: ICONS[m.name] || CreditCardIcon,
                    recordable: !SPECIAL.includes(m.name),
                }))
        },
        recordableMethods() {
            return this.methods.filter((m) => m.recordable)
        },
        specialMethods() {
            return this.methods.filter((m) => !m.recordable)
        },
        clientName() {
            const c = this.store.currentCustomer
            if (!c || c === false || c.is_guest) return this.$t('checkout.v3.guest_client')
            return [c.first_name, c.last_name].filter(Boolean).join(' ') || this.$t('checkout.v3.guest_client')
        },
        voucherSum() {
            return this.vouchers.reduce((s, v) => s + (Number(v.amount) || 0), 0)
        },
        fidelityRate() {
            return this.store.fidelityRate || 10
        },
        fidelityEuro() {
            return this.round((Number(this.fidelityPoints) || 0) / this.fidelityRate)
        },
        fidelityMaxEuro() {
            return this.round((this.store.loyaltyPoints || 0) / this.fidelityRate)
        },
        paidSum() {
            const cash = Object.values(this.amounts).reduce((s, v) => s + (Number(v) || 0), 0)
            return this.round(
                cash +
                    this.voucherSum +
                    (Number(this.stripeAmount) || 0) +
                    (Number(this.walletAmount) || 0) +
                    this.fidelityEuro +
                    (Number(this.prepaid.coverage) || 0)
            )
        },
        // A valid instant discount: positive amount (>= 0.01, decimals allowed e.g. 7.5);
        // a percentage additionally capped at 100 (the server re-validates both — the front
        // check is only for a friendly, immediate button state).
        validCustomDiscount() {
            const amt = Number(this.customDiscountAmount)
            if (!Number.isFinite(amt) || amt < 0.01) return false
            if (this.customDiscountType === 'percentage' && amt > 100) return false
            return true
        },
        reductionPromos() {
            return (this.store.promoCodes || []).filter((p) => p.back_office_only && !p.disabled)
        },
        otherPromos() {
            return (this.store.promoCodes || []).filter((p) => !p.back_office_only && !p.disabled)
        },
        eligiblePromoTotal() {
            let t = (this.store.lines || []).reduce(
                (s, l) => s + (l.canApplyPromoCode ? (Number(l.price) || 0) * (Number(l.qty) || 1) : 0),
                0
            )
            if (this.appliedPromo && this.appliedPromo.has_voucher) {
                t += (this.store.drafts || []).reduce((s, d) => s + (Number(d.amount) || 0) * (Number(d.qty) || 1), 0)
            }
            return this.round(t)
        },
        promoDiscount() {
            if (!this.appliedPromo) return 0
            // One source or the other, never both — else a persisted promo would be counted twice
            // (once in discountTotal, once by re-deriving it here from discount_amount).
            // The server number is also the more correct one: applyForOrder() prorated a fixed promo
            // across the eligible lines and applied the real per-item eligibility, whereas
            // eligiblePromoTotal only approximates that with the canApplyPromoCode flag.
            if (this.promoFromServer) return this.round(this.store.discountTotal)
            const base = this.eligiblePromoTotal
            const amt = Number(this.appliedPromo.discount_amount) || 0
            if (this.appliedPromo.discount_type === 'fixed') return this.round(Math.min(amt, base))
            return this.round((base * amt) / 100)
        },
        effectiveTotal() {
            return Math.max(0, this.round(this.store.total - this.promoDiscount))
        },
        remaining() {
            return Math.max(0, this.round(this.effectiveTotal - this.store.currentPrice - this.paidSum))
        },
        // Trop-perçu : le déjà-payé (acompte) + les paiements en cours dépassent le net dû. Sert
        // uniquement à afficher un avertissement (rembourser l'excédent) — ne bloque rien.
        overpaid() {
            return Math.max(0, this.round(this.store.currentPrice + this.paidSum - this.effectiveTotal))
        },
        solde() {
            // "Rien à payer" : le déjà-payé + les paiements en cours couvrent le net dû. Le `> 0` d'avant
            // bloquait le cas où le net est nul (promo 100 %) — la vente est pourtant soldée et clôturable.
            // On exige juste un vrai panier (lignes ou acompte) pour ne pas afficher « soldé » sur un
            // panier vide.
            const covered = this.store.currentPrice + this.paidSum + 0.001 >= this.effectiveTotal
            const hasSale = this.store.count > 0 || this.store.currentPrice > 0
            return covered && hasSale
        },
        // Vente clôturée (status_id 7 / signée) : document fiscal figé (NF525). Aucun encaissement,
        // aucune re-clôture, aucune promo. Le remboursement (avoir) reste autorisé.
        locked() {
            return !!this.store.isClotured
        },
        canEncaisser() {
            return this.paidSum > 0.001 && !this.store.busy && !this.locked
        },
        canCloturer() {
            return this.solde && !this.store.busy && !this.locked
        },
    },
    watch: {
        open(v) {
            if (v) {
                this.resetAmounts()
                this.initRefunds()
            }
        },
        'store.invoices': {
            handler() {
                this.initRefunds()
            },
            deep: true,
        },
    },
    mounted() {
        this.resetAmounts()
    },
    methods: {
        resetAmounts() {
            const a = {}
            this.recordableMethods.forEach((m) => {
                a[m.name] = 0
            })
            this.amounts = a
            this.vouchers = []
            this.voucherCode = ''
            this.cards = []
            this.cardsLoaded = false
            this.selectedCard = null
            this.stripeAmount = 0
            this.walletAmount = 0
            this.fidelityPoints = 0
            this.selectedPromoCode = ''
            this.customDiscountType = 'fixed'
            this.customDiscountAmount = null
            // Re-hydrate the promotion the order ALREADY carries (server-derived from the
            // promotions_orders pivot). Without this, appliedPromo is null on every reopen, so
            // submitSale() sends promoCodesImpact='[]' and applyPromoCodes() — which deletes every
            // pivot row before re-applying what it is sent — silently wipes the discount and
            // resets order->remise_price to 0, charging the customer full price.
            this.appliedPromo = this.store.promotion ? { ...this.store.promotion } : null
            this.promoFromServer = !!this.store.promotion
            this.prepaid = { prepaidSerie: [], prepaidTreatment: [], coverage: 0 }
            this.invoice = { send_invoice: false, company_name: '', company_address: '', company_cp: '', company_country: '' }
            this.sendMail = false
            this.notice = false
        },
        onPrepaidChange(payload) {
            this.prepaid = {
                prepaidSerie: payload.prepaidSerie || [],
                prepaidTreatment: payload.prepaidTreatment || [],
                coverage: Number(payload.coverage) || 0,
            }
        },
        capWallet() {
            const v = Number(this.walletAmount) || 0
            if (v > this.store.walletBalance) this.walletAmount = this.store.walletBalance
            if (v < 0) this.walletAmount = 0
        },
        fillWallet() {
            const want = (Number(this.walletAmount) || 0) + this.remaining
            this.walletAmount = this.round(Math.min(this.store.walletBalance, want))
        },
        capFidelity() {
            let v = Math.floor(Number(this.fidelityPoints) || 0)
            if (v > this.store.loyaltyPoints) v = this.store.loyaltyPoints
            if (v < 0) v = 0
            this.fidelityPoints = v
        },
        promoLabel(p) {
            const amt = `${p.discount_amount}${p.discount_type === 'fixed' ? ' €' : ' %'}`
            return `${p.code} — ${p.title} · ${amt}${p.is_global ? ' (global)' : ''}`
        },
        // What to display for an applied promotion. A hidden "remise instantanée" (is_special) NEVER
        // shows its generated code — only its title. A normal promo shows its code.
        promoDisplay(promo) {
            if (!promo) return ''
            return promo.is_special ? promo.title : promo.code
        },
        // Apply an ad-hoc "remise instantanée": the server creates a hidden single-use promotion and
        // applies it, then we rehydrate exactly like applyPromo() (server truth via promoFromServer).
        async applyCustomDiscount() {
            if (!this.validCustomDiscount || this.promoBusy) return
            // Only one promotion per order (applyForOrder() throws code 5 otherwise).
            if (this.promoFromServer) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.promo_already_applied'))
                return
            }
            this.promoBusy = true
            try {
                const res = await this.store.applyCustomPromotion(this.customDiscountType, Number(this.customDiscountAmount))
                if (!res.ok) {
                    this.$toast && this.$toast.error(res.message || this.$t('checkout.v3.error_promo_failed'))
                    return
                }
                this.appliedPromo = this.store.promotion ? { ...this.store.promotion } : this.appliedPromo
                this.promoFromServer = true
                this.customDiscountAmount = null
                this.$toast && this.$toast.success(this.$t('checkout.v3.custom_discount_applied'))
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_promo_failed'))
            } finally {
                this.promoBusy = false
            }
        },
        async applyPromo() {
            const code = this.selectedPromoCode
            if (!code || this.promoBusy) return
            // Only one promotion per order (applyForOrder() throws code 5 otherwise).
            if (this.promoFromServer) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.promo_already_applied'))
                return
            }
            this.promoBusy = true
            try {
                // Client-side pre-check for a friendly min/max message; the server re-validates.
                const look = await this.store.lookupPromo(code)
                if (look.ok) {
                    const p = look.promo
                    const orderTotal = this.store.total
                    if (p.min_amount && parseFloat(p.min_amount) > orderTotal) {
                        this.$toast &&
                            this.$toast.error(
                                this.$t('checkout.v3.error_min_amount', { min: parseFloat(p.min_amount).toFixed(2) })
                            )
                        return
                    }
                    if (p.max_amount && parseFloat(p.max_amount) > 0 && parseFloat(p.max_amount) < orderTotal) {
                        this.$toast &&
                            this.$toast.error(
                                this.$t('checkout.v3.error_max_amount', { max: parseFloat(p.max_amount).toFixed(2) })
                            )
                        return
                    }
                }
                // Persist NOW so the promo survives a page refresh (writes promotions_orders +
                // remise_price + promo_code server-side, then re-hydrates the store).
                const res = await this.store.applyPromotion(code)
                if (!res.ok) {
                    this.$toast && this.$toast.error(res.message || this.$t('checkout.v3.error_promo_failed'))
                    return
                }
                this.appliedPromo = this.store.promotion ? { ...this.store.promotion } : this.appliedPromo
                this.promoFromServer = true
                this.selectedPromoCode = ''
                this.$toast && this.$toast.success(this.$t('checkout.v3.promo_applied', { code }))
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_promo_failed'))
            } finally {
                this.promoBusy = false
            }
        },
        async removePromo() {
            if (this.promoBusy) return
            this.promoBusy = true
            try {
                // Persisted promo: drop it server-side (deletes the pivot + clears remise_price/promo_code).
                if (this.promoFromServer) {
                    const res = await this.store.removePromotion()
                    if (!res.ok) {
                        this.$toast && this.$toast.error(res.message || this.$t('checkout.v3.error_promo_failed'))
                        return
                    }
                }
                this.appliedPromo = null
                this.promoFromServer = false
                this.selectedPromoCode = ''
            } finally {
                this.promoBusy = false
            }
        },
        initRefunds() {
            const m = {}
            ;(this.store.invoices || []).forEach((i) => {
                m[i.id] = i.amount
            })
            this.refundAmounts = m
        },
        async refund(inv) {
            const amount = Number(this.refundAmounts[inv.id])
            if (!amount || amount <= 0 || amount > inv.amount + 0.001) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_invalid_refund_amount'))
                return
            }
            try {
                await this.store.refundInvoice(inv.id, this.round(amount))
                this.$toast && this.$toast.success(this.$t('checkout.v3.refund_success'))
            } catch (e) {
                // Afficher le motif concret renvoyé par le back (ex. refus Stripe traduit),
                // et non un message générique — cohérent avec PaymentRefound.vue.
                const message = e?.response?.data?.message || this.$t('checkout.v3.error_refund_failed')
                this.$toast && this.$toast.error(message)
            }
        },
        async verifyVoucher() {
            const code = this.voucherCode
            if (!code || this.voucherBusy) return
            this.voucherBusy = true
            try {
                const res = await this.store.checkVoucher(code, this.vouchers.length + 1)
                if (!res.ok) {
                    this.$toast && this.$toast.error(res.message)
                    return
                }
                if (this.vouchers.some((v) => v.id === res.voucher.id)) {
                    this.$toast && this.$toast.info(this.$t('checkout.v3.error_voucher_already_added'))
                    return
                }
                const amount = Math.min(res.voucher.available, this.remaining || res.voucher.available)
                this.vouchers.push({ ...res.voucher, amount: this.round(amount) })
                this.voucherCode = ''
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_voucher_failed'))
            } finally {
                this.voucherBusy = false
            }
        },
        removeVoucher(i) {
            this.vouchers.splice(i, 1)
        },
        async loadCards() {
            if (this.cardsLoading) return
            this.cardsLoading = true
            try {
                this.cards = await this.store.loadCards(this.store.userId)
                this.cardsLoaded = true
            } catch (e) {
                this.cards = []
                this.cardsLoaded = true
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_loading_cards'))
            } finally {
                this.cardsLoading = false
            }
        },
        round(v) {
            return Math.round((Number(v) || 0) * 100) / 100
        },
        formatPrice(v) {
            return this.round(v).toFixed(2).replace('.', ',')
        },
        fill(name) {
            const current = Number(this.amounts[name]) || 0
            this.$set(this.amounts, name, this.round(current + this.remaining))
        },
        clear(name) {
            this.$set(this.amounts, name, 0)
        },
        close() {
            this.$emit('close')
        },
        // Clic sur « Clôturer ». Si le client a trop payé, on demande confirmation d'abord ; sinon on
        // clôture directement. (Aucun blocage : le caissier peut confirmer.)
        requestClose() {
            if (!this.canCloturer) return
            if (this.overpaid > 0.009) {
                this.confirmCloseOpen = true
                return
            }
            this.validate(true)
        },
        confirmClose() {
            this.confirmCloseOpen = false
            this.validate(true)
        },
        async validate(closeSale) {
            if (closeSale ? !this.canCloturer : !this.canEncaisser) return
            const stripe = this.round(this.stripeAmount)
            if (stripe > 0) {
                if (!this.selectedCard) {
                    this.$toast && this.$toast.error(this.$t('checkout.v3.error_choose_card'))
                    return
                }
                const charge = await this.store.chargeStripe({ cartId: this.selectedCard, montant: stripe })
                if (!charge.ok) {
                    this.$toast && this.$toast.error(charge.message)
                    return
                }
            }
            const payments = {}
            Object.keys(this.amounts).forEach((k) => {
                payments[k] = this.round(this.amounts[k])
            })
            if (stripe > 0) payments['Stripe'] = stripe
            const wallet = this.round(this.walletAmount)
            if (wallet > 0) payments['Wallet'] = wallet
            const vouchers = this.vouchers
                .filter((v) => Number(v.amount) > 0)
                .map((v) => ({ id: v.id, amount: this.round(v.amount), type: 'Bon cadeau' }))
            const fidelityPoints = Math.floor(Number(this.fidelityPoints) || 0)

            const res = await this.store.submitSale(payments, {
                closeSale,
                vouchers,
                fidelityPoints,
                promo: this.appliedPromo,
                prepaidSerie: this.prepaid.prepaidSerie,
                prepaidTreatment: this.prepaid.prepaidTreatment,
                invoice: this.invoice.send_invoice ? this.invoice : null,
                sendMail: this.sendMail,
                notice: this.sendMail && this.notice,
            })
            if (res && res.ok) {
                this.$emit('paid', { closeSale })
            } else {
                this.$toast && this.$toast.error(res?.reason || this.$t('checkout.v3.error_payment_failed'))
            }
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(3, 6, 20, 0.7);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.cv3-modal {
    position: relative;
    width: 100%;
    max-width: 980px;
    max-height: 92vh;
    background: var(--cv3-panel);
    border: 1px solid var(--cv3-border-input);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
    color: var(--cv3-text);
}
.cv3-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 22px;
    border-bottom: 1px solid var(--cv3-border);
}
.cv3-modal-title {
    display: flex;
    align-items: center;
    gap: 12px;
    h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
    }
}
.cv3-modal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: var(--cv3-accent);
    color: #fff;
}
.cv3-modal-close {
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    display: inline-flex;
    cursor: pointer;
}
.cv3-modal-body {
    flex: 1 1 auto;
    overflow: auto;
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 0;
}
.cv3-recap {
    padding: 20px 22px;
    border-right: 1px solid var(--cv3-border);
    display: flex;
    flex-direction: column;
}
.cv3-recap-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
}
.cv3-recap-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--cv3-text-2);
}
.cv3-recap-client {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: var(--cv3-text);
}
.cv3-recap-lines {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.cv3-recap-line {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: 10px;
}
.cv3-recap-line-main {
    display: flex;
    gap: 8px;
    align-items: baseline;
}
.cv3-recap-qty {
    color: var(--cv3-muted);
    font-size: 13px;
}
.cv3-recap-name {
    font-size: 14px;
    font-weight: 600;
}
.cv3-recap-total {
    font-weight: 700;
    text-align: right;
    display: inline-flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 6px;
}
.cv3-recap-unit {
    grid-column: 1 / 2;
    font-size: 11px;
    color: var(--cv3-muted);
}
.cv3-recap-old {
    font-weight: 600;
    color: var(--cv3-muted);
}
.cv3-recap-total .cv3-recap-old {
    font-size: 12px;
}
.cv3-recap-promo {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgba(74, 222, 128, 0.12);
    border: 1px solid rgba(74, 222, 128, 0.35);
    color: #4ade80;
    font-size: 10px;
    font-weight: 700;
}
.cv3-recap-remise {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    color: #4ade80;
    font-weight: 600;
    font-size: 13px;
}
.cv3-deja {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid var(--cv3-border);
}
.cv3-deja-label {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: var(--cv3-muted);
}
.cv3-deja-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
}
.cv3-deja-info {
    font-size: 13px;
    font-weight: 600;
    small {
        color: var(--cv3-muted);
        font-weight: 400;
    }
}
.cv3-deja-amount {
    margin-left: auto;
    font-weight: 700;
    font-size: 13px;
}
.cv3-refund {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    input {
        width: 70px;
        text-align: right;
        background: var(--cv3-input);
        border: 1px solid var(--cv3-border-input);
        border-radius: 8px;
        color: var(--cv3-text);
        padding: 5px 7px;
        font-size: 12px;
    }
}
.cv3-refund-btn {
    border: 1px solid rgba(255, 107, 107, 0.4);
    background: rgba(255, 107, 107, 0.12);
    color: #ff8787;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
}
.cv3-recap-foot {
    margin-top: 12px;
    padding-top: 14px;
    border-top: 1px solid var(--cv3-border);
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}
.cv3-recap-grand {
    font-size: 22px;
    font-weight: 700;
}

.cv3-pay {
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.cv3-status {
    border-radius: 12px;
    padding: 16px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid;
    &.solde {
        background: rgba(34, 197, 94, 0.12);
        border-color: rgba(34, 197, 94, 0.5);
        color: #4ade80;
    }
    &.reste {
        background: rgba(234, 179, 8, 0.12);
        border-color: rgba(234, 179, 8, 0.5);
        color: #facc15;
    }
}
.cv3-status-label {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.4px;
}
.cv3-status-val {
    font-size: 26px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
}
.cv3-pay-label {
    margin: 4px 0 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: var(--cv3-muted);
}
.cv3-empty {
    color: var(--cv3-muted);
    font-size: 13px;
}
.cv3-pay-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 13px 15px;
    border-radius: 12px;
    border: 1px solid var(--cv3-border-input);
    background: rgba(255, 255, 255, 0.02);
    color: var(--cv3-text);
    cursor: pointer;
    &.primary.filled {
        border-color: var(--cv3-accent);
        background: var(--cv3-icon-tint);
    }
    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
.cv3-pay-name {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 14px;
    font-weight: 600;
}
.cv3-pay-amount {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    input {
        width: 86px;
        text-align: right;
        background: var(--cv3-input);
        border: 1px solid var(--cv3-border-input);
        border-radius: 8px;
        color: var(--cv3-text);
        padding: 6px 8px;
        font-weight: 700;
        -moz-appearance: textfield;
        appearance: none;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}
.cv3-cur {
    color: var(--cv3-muted);
    font-weight: 700;
}
.cv3-soon {
    color: var(--cv3-muted);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}
.cv3-clear {
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    display: inline-flex;
    cursor: pointer;
}
.cv3-special-block {
    border: 1px solid var(--cv3-border-input);
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.cv3-special-head {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 14px;
    font-weight: 600;
}
.cv3-bal {
    margin-left: auto;
    color: var(--cv3-muted);
    font-weight: 600;
    font-size: 12px;
}
.cv3-special-row {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: space-between;
}
.cv3-eur-eq {
    color: var(--cv3-muted);
    font-size: 13px;
    font-weight: 600;
}
.cv3-check {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 13px;
    font-weight: 600;
    color: var(--cv3-text);
    cursor: pointer;
    input[type='checkbox'] {
        width: 16px;
        height: 16px;
        accent-color: var(--cv3-accent);
    }
    &.sub {
        margin-left: 26px;
        font-weight: 500;
        color: var(--cv3-text-2);
    }
}
.cv3-inv-input {
    width: 100%;
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 8px;
    color: var(--cv3-text);
    padding: 8px 10px;
    font-size: 13px;
    &::placeholder {
        color: var(--cv3-muted);
    }
}
.cv3-inv-row {
    display: flex;
    gap: 8px;
}
.cv3-voucher-entry {
    display: flex;
    gap: 8px;
    input {
        flex: 1 1 auto;
        background: var(--cv3-input);
        border: 1px solid var(--cv3-border-input);
        border-radius: 8px;
        color: var(--cv3-text);
        padding: 8px 10px;
        font-size: 13px;
        &::placeholder {
            color: var(--cv3-muted);
        }
    }
    button {
        border: 0;
        border-radius: 8px;
        background: var(--cv3-accent);
        color: #fff;
        font-weight: 600;
        font-size: 13px;
        padding: 0 14px;
        cursor: pointer;
    }
}
.cv3-voucher-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: var(--cv3-icon-tint);
    border-radius: 8px;
    padding: 8px 10px;
}
.cv3-voucher-code {
    font-size: 13px;
    font-weight: 600;
    small {
        color: var(--cv3-muted);
        font-weight: 400;
    }
}
.cv3-mini-btn {
    border: 1px solid var(--cv3-border-input);
    background: transparent;
    color: var(--cv3-text-2);
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.cv3-soon-text {
    margin: 0;
    color: var(--cv3-muted);
    font-size: 13px;
}
.cv3-stripe {
    display: flex;
    gap: 8px;
    align-items: center;
}
.cv3-stripe-card {
    flex: 1 1 auto;
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 8px;
    color: var(--cv3-text);
    padding: 8px 10px;
    font-size: 13px;
}
.cv3-promo-pick {
    display: flex;
    gap: 8px;
}
.cv3-promo-pick select {
    flex: 1 1 auto;
}
.cv3-promo-custom {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}
.cv3-promo-custom .cv3-custom-type {
    flex: 0 0 64px;
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 8px;
    color: var(--cv3-text);
    padding: 8px 10px;
    font-size: 13px;
}
.cv3-promo-custom input {
    flex: 1 1 auto;
    background: var(--cv3-input);
    border: 1px solid var(--cv3-border-input);
    border-radius: 8px;
    color: var(--cv3-text);
    padding: 8px 10px;
    font-size: 13px;
}
.cv3-promo-applied {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--cv3-icon-tint);
    border: 1px solid var(--cv3-accent);
}
.cv3-promo-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 13px;
    color: var(--cv3-text);
}
.cv3-pay-locked {
    pointer-events: none;
    opacity: 0.4;
    filter: grayscale(0.6);
    user-select: none;
}
.cv3-locked-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 9px 12px;
    border-radius: 10px;
    background: rgba(250, 204, 21, 0.1);
    border: 1px solid rgba(250, 204, 21, 0.35);
    color: #facc15;
    font-size: 12px;
    font-weight: 700;
}
.cv3-overpaid-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(248, 113, 113, 0.12);
    border: 1px solid rgba(248, 113, 113, 0.4);
    color: #f87171;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
}
.cv3-confirm-overlay {
    position: absolute;
    inset: 0;
    z-index: 20;
    background: rgba(3, 6, 20, 0.72);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    border-radius: 20px;
}
.cv3-confirm {
    width: 100%;
    max-width: 420px;
    background: var(--cv3-panel);
    border: 1px solid var(--cv3-border-input);
    border-radius: 16px;
    padding: 22px;
}
.cv3-confirm-head {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #f87171;
    margin-bottom: 12px;
    h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: var(--cv3-text);
    }
}
.cv3-confirm-msg {
    margin: 0 0 20px;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--cv3-text-2);
}
.cv3-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
.cv3-confirm-cancel,
.cv3-confirm-ok {
    padding: 10px 16px;
    border-radius: 11px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}
.cv3-confirm-cancel {
    background: transparent;
    border: 1px solid var(--cv3-border-input);
    color: var(--cv3-text);
}
.cv3-confirm-ok {
    background: #f87171;
    border: 0;
    color: #fff;
    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
}
.cv3-modal-foot {
    padding: 16px 22px;
    border-top: 1px solid var(--cv3-border);
    display: flex;
    gap: 10px;
}
.cv3-acompte-btn {
    flex: 0 0 auto;
    padding: 15px 20px;
    border-radius: 13px;
    border: 1px solid var(--cv3-border-input);
    background: transparent;
    color: var(--cv3-text-2);
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
}
.cv3-validate-pay {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px;
    border-radius: 13px;
    border: 0;
    background: var(--cv3-accent);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    box-shadow: 0 9px 23px -7px rgba(125, 45, 189, 0.7);
    cursor: pointer;
    &:disabled {
        box-shadow: none;
        opacity: 0.6;
        cursor: not-allowed;
    }
}
</style>
